"use client";

import Script from "next/script";
import { useEffect } from "react";

import { useOnlineStatus } from "@/app/hooks/useOnlineStatus";
import { useSession } from "@/app/hooks/useSession";
import { identifyUser, processAnalyticsQueue, trackSigninCompleted } from "@/lib/analytics";
import { IS_PRODUCTION } from "@/lib/env";

/**
 * Umami Analytics Tracker Component
 * Loads Umami tracking script via local proxy (no CORS issues!)
 * Script is served from /api/analytics/script.js which proxies to your Umami server
 */
export function UmamiTracker() {
  const { data: session, isAuthenticated, isPremium } = useSession();
  const { isOffline } = useOnlineStatus();

  // Identify user when they sign in
  useEffect(() => {
    if (isAuthenticated && session?.user?.id) {
      const user = session.user as any;
      identifyUser(session.user.id, {
        isPremium,
        provider: user.provider,
      });
      if (user.isNewSession) {
        trackSigninCompleted({ provider: user.provider ?? "unknown" });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, session?.user?.id, isPremium]);

  // Process analytics queue when app goes into background
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        processAnalyticsQueue();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Check if analytics is enabled (requires UMAMI_WEBSITE_ID in production)
  const websiteId = process.env.NEXT_PUBLIC_UMAMI_WEBSITE_ID;
  const isEnabled = !!websiteId;

  if (!isEnabled || isOffline) {
    if (IS_PRODUCTION) {
      console.log("📊 Umami analytics: Disabled (missing NEXT_PUBLIC_UMAMI_WEBSITE_ID)");
    }
    return null;
  }

  return (
    <Script
      defer
      src="/api/analytics/script.js"
      data-website-id={websiteId}
      data-host-url={typeof window !== "undefined" ? window.location.origin : ""}
      strategy="afterInteractive"
    />
  );
}
